import {
  AppBadge,
  AppCard,
  AppIconTile,
  Box,
  Button,
  Checkbox,
  DataTable,
  EmptyState,
  FormControlLabel,
  PageHeader,
  SectionCard,
  SimpleCard,
  Stack,
  Switch,
  TextField,
  Typography
} from "@app/ui-kit";
import { useState } from "react";
import { APP_DEFINITIONS } from "../apps/registry";
import { RecordActions } from "../components/RecordActions";

const cardSamples = [
  {
    eyebrow: "Engagement",
    title: "Technology Diligence",
    text: "Independent assessment of technology posture for investment committees and boards."
  },
  {
    eyebrow: "Insights",
    title: "Operating Cadence",
    text: "Weekly read on adoption, throughput, and execution risk across the portfolio."
  },
  {
    eyebrow: "Finance",
    title: "Engagement Status",
    text: "Current scope, retainer schedule, and outstanding deliverables at a glance."
  },
  {
    eyebrow: "Reliability",
    title: "Operational Posture",
    text: "Uptime, incident history, and outstanding remediation items by surface."
  },
  {
    eyebrow: "Workstream",
    title: "Open Decisions",
    text: "The decisions awaiting principal attention this week — nothing more."
  },
  {
    eyebrow: "Memo",
    title: "Recent Briefings",
    text: "Diligence memos and operating briefings shared with the board this quarter."
  }
];

const tableRows = cardSamples.map((card) => ({
  card: (
    <SimpleCard
      eyebrow={card.eyebrow}
      title={card.title}
      text={card.text}
      sx={{ minWidth: 320 }}
    />
  )
}));

export const UiKitGallery = () => {
  const [lastAction, setLastAction] = useState<string | null>(null);
  return (
    <Stack spacing={5}>
      <PageHeader
        eyebrow="Design System"
        title="UI Kit"
        subtitle="Foundations and components from the BKDP design system, applied to the host shell."
      />

      <SectionCard eyebrow="Buttons" title="Action Hierarchy">
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Button variant="contained">Start a conversation</Button>
          <Button variant="outlined">Review samples</Button>
          <Button variant="text">Read the diligence brief →</Button>
        </Stack>
      </SectionCard>

      <SectionCard eyebrow="Record pages" title="Record Actions">
        {/* ACT-1: host-shell component, shown here so the ranking can be reviewed in isolation. */}
        <Stack spacing={2} alignItems="flex-start">
          <RecordActions
            primary={{ label: "Open portal", onClick: () => setLastAction("Open portal") }}
            items={[
              { label: "Copy portal link", onClick: () => setLastAction("Copy portal link") },
              { label: "Export members", onClick: () => setLastAction("Export members"), disabled: true, tooltip: "Nothing to export until the client has members" },
              { label: "Suspend", destructive: true, onClick: () => setLastAction("Suspend") }
            ]}
          />
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {lastAction ? `Last action: ${lastAction}` : "No action taken yet"}
          </Typography>
        </Stack>
      </SectionCard>

      <SectionCard eyebrow="Form" title="Inputs">
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <TextField label="Full name" placeholder="Alexandra Chen" fullWidth />
          <TextField
            label="Company email"
            type="email"
            placeholder="a.chen@fund.com"
            fullWidth
          />
        </Stack>
        <Stack direction="row" spacing={3} mt={3}>
          <FormControlLabel control={<Checkbox />} label="Subscribe to briefings" />
          <FormControlLabel control={<Switch />} label="Confidential mode" />
        </Stack>
      </SectionCard>

      <SectionCard eyebrow="Status" title="Badges">
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <AppBadge label="Neutral" />
          <AppBadge label="Active" color="primary" />
          <AppBadge label="Featured" color="secondary" />
          <AppBadge label="In Review" color="info" />
          <AppBadge label="On Track" color="success" />
          <AppBadge label="At Risk" color="warning" />
          <AppBadge label="Blocked" color="error" />
        </Box>
      </SectionCard>

      <SectionCard eyebrow="Cards" title="Surfaces">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 3
          }}
        >
          <AppCard
            eyebrow="Engagement"
            title="Technology Diligence"
            subtitle="Independent assessment of technology posture for investment committees. Two-week standard scope."
          />
          <AppCard
            eyebrow="Positioning"
            title="Featured Card"
            subtitle="Navy variant for principal positioning statements, testimonials, or emphasis moments inside a grid."
            featured
          />
        </Box>
      </SectionCard>

      <SectionCard eyebrow="Applications" title="Icon Tile">
        {/* NAV-4: the five registry marks read from APP_DEFINITIONS, then the neutral fallback that no screen
            otherwise renders, at both sizes. No names beside the tiles; the cards section above carries those. */}
        <Stack spacing={2}>
          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
              Card, 44 px
            </Typography>
            <Box sx={{ display: "flex", gap: 3, alignItems: "center" }}>
              {APP_DEFINITIONS.map((def) => (
                <AppIconTile key={def.slug} icon={def.icon} color={def.iconColor} size="card" />
              ))}
              <AppIconTile />
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
              Navigation, 24 px
            </Typography>
            <Box sx={{ display: "flex", gap: 3, alignItems: "center" }}>
              {APP_DEFINITIONS.map((def) => (
                <AppIconTile key={def.slug} icon={def.icon} color={def.iconColor} size="nav" />
              ))}
              <AppIconTile size="nav" />
            </Box>
          </Box>
        </Stack>
      </SectionCard>

      <SectionCard eyebrow="State" title="Empty State">
        <EmptyState
          eyebrow="No Results"
          title="Nothing to review yet"
          description="Adjust the filter, or open a new engagement to populate this view."
          action={<Button variant="contained">Open engagement</Button>}
        />
      </SectionCard>

      <SectionCard eyebrow="Data" title="Card Table">
        <DataTable
          columns={[{ key: "card", label: "Engagement Streams" }]}
          rows={tableRows}
          maxHeight={420}
          stickyHeader
        />
      </SectionCard>
    </Stack>
  );
};
